'use es6';

import { Axis, XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { getDateResolution, getReportColorEncoding, getReportXEncoding, getReportYEncoding, getReportYMultiEncoding } from '../../utils/report-utils';
import { applyScaleToAxis } from '../apply-scale-to-axis';
import { applyTypeToAxis } from '../apply-type-to-axis';
import { doMeasuresHaveSameFormattingType, getGoalLayer, getMeasureAxisLabel, getMultiMeasureFormatting, getTodayLine, toXYColor } from './shared';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { AccumulateAlong, AccumulateTransform } from 'reporting-visualizations/transforms/transformRecords';
import { reportSupportsGoals } from '../../resolve/goal';
import { getEncodingColumnAlias, getEncodingFormatParameters, getEncodingFormatType } from '../../utils/visual-utils';
import { List } from 'immutable';
import { processReportStacking } from './shared';
import { getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
const mapSingleMeasureEncodingVisual = (report, datasets, fiscalYearMonthName, brandKitPalettes) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const x = getReportXEncoding(report);
  const y = getReportYEncoding(report);
  const color = getReportColorEncoding(report);
  const {
    showDataLabels,
    showMarkers,
    stacking: stackingOption,
    stackingType,
    showToday,
    yMinMax,
    yAxisType,
    color: colorOption,
    goalLineValue,
    legendPosition,
    legendWidth,
    accumulate
  } = report.visual.options;
  const stacking = processReportStacking(stackingOption, stackingType);
  const yAxisKey = 'left';
  const xField = x && x.column.alias;
  const yField = y && y.column.alias;
  const colorField = color && color.column.alias;
  const accumulateAlong = AccumulateAlong({
    field: xField,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49562/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  const primaryLayer = {
    dataset: 'primary',
    mark: {
      type: 'area',
      stacking,
      marker: {
        enabled: showMarkers
      }
    },
    x: x ? {
      type: 'category',
      field: xField,
      blend: 'bottom',
      order: toVisualizationOrder(x.column, primaryDataset),
      formattingType: getEncodingFormatType(x),
      formattingObject: getEncodingFormatParameters(x)
    } : {
      type: 'scaffold',
      blend: 'bottom'
    },
    y: y ? {
      type: 'linear',
      field: yField,
      fill: true,
      blend: yAxisKey,
      formattingType: getEncodingFormatType(y),
      formattingObject: getEncodingFormatParameters(y)
    } : {
      type: 'scaffold',
      blend: yAxisKey
    },
    color: toXYColor(report.columns.get(colorField), yField && List.of(report.columns.get(yField)), primaryDataset, colorOption, color, brandKitPalettes),
    transform: accumulate ? [AccumulateTransform({
      groupBy: [colorField],
      field: yField,
      along: accumulateAlong
    })] : []
  };
  const layers = goalLineValue && reportSupportsGoals(report) ? [getGoalLayer(showMarkers, x, primaryDataset, color, accumulate, accumulateAlong), primaryLayer] : [primaryLayer];
  const xColumn = report.columns.get(xField);
  const dateResolution = getDateResolution(xField, report);
  return XYVisualization({
    type: 'XY',
    data: {
      primary: primaryDataset
    },
    style: {
      dataLabels: showDataLabels,
      legendPosition,
      legendWidth,
      x: showToday && xColumn && dateResolution ? {
        bottom: {
          rules: {
            plotLines: [getTodayLine(xColumn, fiscalYearMonthName)]
          }
        }
      } : undefined,
      y: {
        [yAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), yMinMax), yAxisType)
      },
      dateResolution
    },
    layers
  });
};
const mapMultiMeasureEncodingVisual = (report, datasets, fiscalYearMonthName, brandKitPalettes) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const x = getReportXEncoding(report);
  const yMulti = getReportYMultiEncoding(report);
  const color = getReportColorEncoding(report);
  const {
    showDataLabels,
    stacking: stackingOption,
    stackingType,
    showMarkers,
    showToday,
    yMinMax,
    yAxisType,
    color: colorOption,
    goalLineValue,
    legendPosition,
    legendWidth,
    accumulate,
    yAxisLabel: labelSetting
  } = report.visual.options;
  const stacking = processReportStacking(stackingOption, stackingType);
  const yAxisKey = 'left';
  const yAxisLabel = getMeasureAxisLabel(labelSetting, yMulti, primaryDataset);
  const xField = x && getEncodingColumnAlias(x);
  const colorField = color && getEncodingColumnAlias(color);
  const dateResolution = getDateResolution(xField, report);
  const accumulateAlong = AccumulateAlong({
    field: xField,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49562/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  const allMeasuresHaveSameFormattingType = doMeasuresHaveSameFormattingType(yMulti, primaryDataset);
  const formattingObjectMap = getMultiMeasureFormatting(yMulti);
  const useMultiEncodingFormattingObject = yMulti.size > 1;
  const primaryLayers = yMulti.map(yEncoding => {
    const yField = getEncodingColumnAlias(yEncoding);
    return {
      dataset: 'primary',
      mark: {
        type: 'area',
        stacking,
        marker: {
          enabled: showMarkers
        }
      },
      x: x ? {
        type: 'category',
        field: xField,
        blend: 'bottom',
        order: toVisualizationOrder(x.column, primaryDataset),
        formattingType: getEncodingFormatType(x),
        formattingObject: getEncodingFormatParameters(x)
      } : {
        type: 'scaffold',
        blend: 'bottom'
      },
      y: {
        type: 'linear',
        field: yField,
        blend: yAxisKey,
        fill: true,
        formattingObjectMap: useMultiEncodingFormattingObject ? formattingObjectMap : undefined,
        formattingType: allMeasuresHaveSameFormattingType ? getEncodingFormatType(yEncoding) : undefined,
        formattingObject: allMeasuresHaveSameFormattingType ? getEncodingFormatParameters(yEncoding) : undefined
      },
      color: toXYColor(color && color.column, yMulti.map(encoding => encoding.column), primaryDataset, colorOption, yMulti.size > 1 ? yEncoding : color, brandKitPalettes),
      transform: accumulate ? [AccumulateTransform({
        groupBy: [colorField],
        field: yField,
        along: accumulateAlong
      })] : []
    };
  });
  const layers = goalLineValue && reportSupportsGoals(report) ? [getGoalLayer(showMarkers, x, primaryDataset, color, accumulate, accumulateAlong), ...primaryLayers] : primaryLayers;
  return XYVisualization({
    type: 'XY',
    data: {
      primary: primaryDataset
    },
    style: {
      dataLabels: showDataLabels,
      legendPosition,
      legendWidth,
      showTotals: allMeasuresHaveSameFormattingType,
      x: showToday && x.column && dateResolution ? {
        bottom: {
          rules: {
            plotLines: [getTodayLine(x.column, fiscalYearMonthName)]
          }
        }
      } : undefined,
      y: {
        [yAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), yMinMax), yAxisType).set('title', yAxisLabel)
      },
      dateResolution
    },
    layers
  });
};
export const mapAreaVisual = (report, datasets, _reportOptions, {
  fiscalYearMonthName,
  brandKitPalettes
}) => {
  const yMulti = getReportYMultiEncoding(report);
  if (yMulti && yMulti.size > 0) {
    return mapMultiMeasureEncodingVisual(report, datasets, fiscalYearMonthName, brandKitPalettes);
  }
  return mapSingleMeasureEncodingVisual(report, datasets, fiscalYearMonthName, brandKitPalettes);
};